import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { controlClassNoHeight } from '../lib/control.js';
import { cn } from '../lib/utils.js';

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Show the invalid treatment. Sets `aria-invalid` so the styling and what
   *  assistive tech is told can never disagree. */
  error?: boolean;
  /**
   * Give the user back the browser's drag handle, on the vertical axis only.
   *
   * OFF by default, which is the opposite of the browser (`resize: vertical`),
   * because the handle is a live defect anywhere near a dialog: the box grows
   * past the modal's own scroll container and the field ends up half outside
   * the window it belongs to. That was fixed by hand on the corrections dialog
   * last week; the default here is that fix, applied everywhere at once.
   *
   * Opt in for a long-form field that lives on a full page rather than in a
   * dialog — a letter template, say — where the user genuinely may want more
   * room than `rows` gives them and there is no modal edge to escape.
   * Horizontal resize is never offered: widening the box breaks the form
   * column it sits in.
   */
  resizable?: boolean;
}

/* ===========================================================================
 * The multi-line member of the control family.
 *
 * WHY IT EXISTS. Sixteen screens hand-rolled `<textarea>` with a local class
 * string, and all sixteen drifted the same way — `rounded-md` (10px) against
 * the control scale's 6px, stock `text-sm` against `text-copy-lg`, and nine of
 * ten with NO focus style whatsoever, so tabbing through a form the field went
 * silently invisible. A primitive is the only thing that makes those agree, and
 * keeps them agreeing.
 *
 * THE BORDER IS THE POINT. Every hand-rolled copy used `border-border`. That is
 * the DECORATIVE hairline: apps/web/src/index.css splits `--border` from
 * `--input` precisely because the two do different jobs, and records the
 * measurements — the hairline is 1.71:1 in light and 2.08:1 in dark, while WCAG
 * 1.4.11 requires 3:1 for the visual boundary of a control. So every one of
 * these boxes had an edge the user could not reliably see. `--input` is solved
 * against the harder of the two grounds it lands on and clears the bar in both
 * themes (3.09:1 / 3.60:1 light, 3.47:1 / 3.08:1 dark). These are the fields
 * people type Emirates ID numbers, rejection reasons and company-wide
 * announcements into; "roughly where the box is" is not good enough.
 *
 * HEIGHT. This is the one thing a textarea cannot share with `<Input>`, so it
 * takes `controlClassNoHeight` and supplies its own:
 *
 *   - `rows` stays the caller's, and stays the primary control. It is the prop
 *     every call site already used and the one that reads correctly — "three
 *     lines of reason", not "68 pixels".
 *   - `py-2` (8px) is the vertical padding. An `<Input>` is 40px tall around a
 *     22.4px line box, which leaves 7.8px above and below; 8px puts a
 *     textarea's FIRST line within a rounding error of where a single-line
 *     field's text sits, so the two line up when they share a form row.
 *   - `min-h-[3.75rem]` is a floor, not a size. 60px is deliberately just under
 *     the smallest deliberate box in the app (`rows={2}` ≈ 63px) so it can
 *     never quietly override a caller's `rows`; it is there for a `<Textarea>`
 *     rendered with no `rows`, or one squeezed by a flex parent, so the control
 *     can never collapse to something a user would read as a text input.
 * ======================================================================== */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, resizable, ...props }, ref) => (
    // `aria-invalid` is written after the spread on purpose — it is derived
    // from `props` already, so letting the spread land on top of it would just
    // undo `error` whenever a caller also passed `aria-invalid={false}`.
    <textarea
      ref={ref}
      className={cn(
        controlClassNoHeight,
        'min-h-[3.75rem] py-2',
        resizable ? 'resize-y' : 'resize-none',
        className
      )}
      {...props}
      aria-invalid={error ? true : props['aria-invalid']}
      // See `Input.tsx` — `required` is mirrored onto `aria-required` on every
      // control in the family so the rule holds everywhere.
      aria-required={props.required ? true : props['aria-required']}
    />
  )
);
Textarea.displayName = 'Textarea';
